// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import React, { CSSProperties } from 'react'
import { DownOutlined, UpOutlined, ArrowUpOutlined } from '@ant-design/icons'
import { Button, Col, Row, List, ConfigProvider, message } from 'antd'
import { HealthCheck } from './HealthCheck'
import { Status } from './Status'
import { FinderSelection } from 'components/camera-finder/FinderSelection'
import { MultipleSelectionModel } from 'utils/multiple-selection-model'
import { CameraListItem } from './CameraListItem'
import { SearchHeader } from 'components/camera-finder/SearchHeader'
import classnames from 'classnames'
import { connect } from 'react-redux'
import './camera-finder.less'
import noResultInView from 'assets/images/camera-finder-empty.png'
import nothingFound from 'assets/images/devices-empty.png'
import { Dispatch, RootState } from 'store'
import { EmptyResult } from 'components/empty-result'
import { getRoadName } from '../../api/devices'

export interface ISearchParam {}

function mapState (state: RootState) {
  return {
    ...state.cameras,
    ...state.download,
    cameraFinderState: state.cameraFinder,
    city: state.city.current
  }
}

function mapDispatch (dispatch: Dispatch) {
  return {
    ...dispatch.cameras,
    ...dispatch.download,
    cameraFinderAction: dispatch.cameraFinder
  }
}

export type ICameraFinderProps = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch> & {
    items: any[]
    selectionModel: MultipleSelectionModel
    style: CSSProperties
    onFilter: () => void
  }

class CameraFinder extends React.Component<ICameraFinderProps> {
  state = {
    isExpanded: false,
    isListScrollTopEdge: true,
    isSelectAll: false
  }

  private timeoutHandler
  private listContainer = React.createRef<HTMLDivElement>()

  componentDidMount () {
    this.props.selectionModel.emitter.on('change', this.handleSelectionChange)
  }

  componentDidUpdate (prevProps) {
    if (this.props.cameraFinderState.expanded !== prevProps.cameraFinderState.expanded) {
      if (this.props.cameraFinderState.expanded) {
        this.listContainer.current.addEventListener('scroll', this.handleListScroll)
      } else if (this.listContainer.current) {
        this.listContainer.current.removeEventListener('scroll', this.handleListScroll)
      }
    }
  }

  componentWillUnmount () {
    if (this.listContainer.current) {
      this.listContainer.current.removeEventListener('scroll', this.handleListScroll)
    }
    this.props.selectionModel.emitter.off('change', this.handleSelectionChange)
  }

  private renderEmpty = () => {
    const { cameraFinderState } = this.props
    let content
    if (cameraFinderState.filter.deviceID) {
      content = <EmptyResult image={nothingFound} desc='Check the cameraID and try searching again' />
    } else {
      content = (
        <EmptyResult image={noResultInView} title='No camera in this map view' desc='Try looking in other map area.' />
      )
    }
    return <div style={{ marginTop: '50%' }}>{content}</div>
  }

  private handleListScroll = e => {
    if (this.listContainer.current.scrollTop === 0) {
      this.setState({
        isListScrollTopEdge: true
      })
    } else if (this.state.isListScrollTopEdge === true) {
      this.setState({
        isListScrollTopEdge: false
      })
    }
  }

  private handleSelectionChange = () => {
    const { selectionModel, updateEditingPreTask, items } = this.props
    this.setState({
      isSelectAll: selectionModel.isAllSelected()
    })
    const selectedIndices = selectionModel.getSelectedIndices()
    updateEditingPreTask({
      deviceIDs: selectedIndices.map(i => items[i].deviceID)
    })
  }

  private scrollToTop = () => {
    this.listContainer.current.scrollTop = 0
  }

  private toggleSelectAll = () => {
    const { selectionModel } = this.props
    const isSelectAll = !this.state.isSelectAll
    this.setState(
      {
        isSelectAll
      },
      () => {
        if (isSelectAll) {
          selectionModel.selectAll()
        } else {
          selectionModel.clearAll()
        }
      }
    )
  }

  private renderContent = (): JSX.Element => {
    const { isListScrollTopEdge, isSelectAll } = this.state
    const { items, selectionModel, cameraFinderState } = this.props
    const operationClassname = classnames('camera-finder-operation', {
      shadow: !isListScrollTopEdge
    })
    return (
      <>
        <div className={operationClassname}>
          <Row align='middle' justify='space-between'>
            <Col>
              <Row gutter={4} align='middle'>
                {/* <Col>
                  <Button onClick={this.toggleSelectAll} type={isSelectAll ? 'primary' : 'default'} size='small'>
                    <CheckOutlined />
                    Select all
                  </Button>
                </Col> */}
                <Col>
                  <HealthCheck onChange={this.handleHealthChange} value={cameraFinderState.filter.status} />
                </Col>
                <Col>
                  <Status onChange={this.handleStatusChange} value={cameraFinderState.filter.onlineStatus} />
                </Col>
              </Row>
            </Col>
            <Col>
              <Button type='text' size='small' onClick={this.toggleExpand}>
                <UpOutlined />
              </Button>
            </Col>
          </Row>
          <Row justify='space-between' align='middle' style={{ marginTop: 13 }}>
            <Col>Showing {items.length} result</Col>
            {!isListScrollTopEdge && (
              <Col>
                <Button type='link' onClick={this.scrollToTop}>
                  <ArrowUpOutlined />
                  Back to top
                </Button>
              </Col>
            )}
          </Row>
        </div>
        <div ref={this.listContainer} onMouseLeave={this.handleListMouseLeave} className='camera-finder-list'>
          <FinderSelection selectionModel={selectionModel}>
            <ConfigProvider renderEmpty={this.renderEmpty}>
              <List
                split
                dataSource={items}
                renderItem={this.renderItem}
                style={{ overflow: 'auto', height: '100%' }}
                loading={cameraFinderState.loading}
              />
            </ConfigProvider>
          </FinderSelection>
        </div>
      </>
    )
  }

  private renderItem = (item, index): JSX.Element => {
    const { selectionModel } = this.props
    return (
      <CameraListItem
        index={index}
        item={item}
        selectionModel={selectionModel}
        onMouseEnter={this.handleItemMouseEnter}
      />
    )
  }

  private handleItemMouseEnter = (item, index) => {
    const { updateHoveredCamera } = this.props
    if (this.timeoutHandler) {
      clearTimeout(this.timeoutHandler)
    }
    //timeout used to hovering clustered cameras more smoothly
    this.timeoutHandler = setTimeout(async () => {
      let roadName = await getRoadName(item.deviceID)
      item.road = roadName
      updateHoveredCamera(item)
    }, 200)
  }

  private handleListMouseLeave = e => {
    const { updateHoveredCamera } = this.props
    this.timeoutHandler = setTimeout(() => {
      updateHoveredCamera(null)
    }, 200)
  }

  private handleHealthChange = async value => {
    const { cameraFinderAction, onFilter } = this.props
    await cameraFinderAction.setFilter({ status: value })
    await onFilter()
  }

  private handleStatusChange = async value => {
    const { cameraFinderAction, onFilter } = this.props
    await cameraFinderAction.setFilter({ onlineStatus: value })
    await onFilter()
  }

  private toggleExpand = () => {
    const { cameraFinderAction, cameraFinderState } = this.props
    cameraFinderAction.setExpanded(!cameraFinderState.expanded)
  }

  private handleCameraIdSearch = async (keyword: string) => {
    const { cameraFinderAction, onFilter } = this.props
    await cameraFinderAction.setFilter({ deviceID: keyword })
    cameraFinderAction.setExpanded(true)
    await onFilter()
  }

  render () {
    const { cameraFinderState, style = {}, city, mode } = this.props
    const rootClassname = classnames('camera-finder', {
      expanded: cameraFinderState.expanded
    })
    const Content = cameraFinderState.expanded ? (
      this.renderContent()
    ) : (
      <Row justify='space-between' className='camera-finder-operation'>
        <Col>See cameras within the current view</Col>
        <Col>
          <Button type='text' size='small' onClick={this.toggleExpand}>
            <DownOutlined />
          </Button>
        </Col>
      </Row>
    )
    return (
      <div style={style} className={rootClassname}>
        <SearchHeader
          onSearch={this.handleCameraIdSearch}
          disabled={mode !== 'add'}
          filterStatus={{
            cityID: city.id,
            status: cameraFinderState.filter.status,
            onlineStatus: cameraFinderState.filter.onlineStatus
          }}
        />
        {Content}
      </div>
    )
  }
}

export default connect(mapState, mapDispatch)(CameraFinder)
